package app.nestbridge.feature.invitations.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CoreCard
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.theme.CoreAlphas
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import com.karawek.core.util.PhoneNumberFormatter
import app.nestbridge.feature.invitations.model.Invitation
import app.nestbridge.feature.invitations.model.InvitationDeliveryMethod
import app.nestbridge.feature.invitations.model.InvitationStatus
import app.nestbridge.feature.careteam.model.TeamRelationshipRole
import app.nestbridge.feature.invitations.screen.ShowInvitationQRCodeEvent
import app.nestbridge.feature.invitations.screen.ShowInvitationQRCodeScreen
import app.nestbridge.feature.invitations.screen.ShowInvitationQRCodeState
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlin.time.Clock

@CircuitInject(
    screen = ShowInvitationQRCodeScreen::class,
    scope = AppScope::class,
)
@Composable
fun ShowInvitationQRCodeUi(
    state: ShowInvitationQRCodeState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        title = "Invitation QR Code",
        subtitle = "Share this QR code with the professional to let them join ${state.invitation.childDisplayName}'s care team.",
        footer = { Footer(state) },
        onBack = { state.eventSink(ShowInvitationQRCodeEvent.Done) },
        onAppear = { state.eventSink(ShowInvitationQRCodeEvent.OnAppear) },
        onAppearKey = state.invitation.id,
        modifier = modifier,
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(CoreSpacing.md),
            modifier = Modifier.fillMaxSize(),
        ) {
            CoreCard(
                modifier = Modifier.fillMaxWidth(),
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(CoreSpacing.xl),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(CoreSpacing.md),
                ) {
                    // Placeholder QR Code
                    Box(
                        modifier = Modifier
                            .size(200.dp)
                            .background(
                                color = CoreTheme.colors.primary.copy(alpha = CoreAlphas.SelectedSurface),
                                shape = RoundedCornerShape(CoreSpacing.md),
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
                        ) {
                            Text(
                                text = "▪️:▪️",
                                style = CoreTypography.display,
                                color = CoreTheme.colors.textSecondary,
                            )
                            Text(
                                text = "QR Code",
                                style = CoreTypography.bodySecondary,
                                color = CoreTheme.colors.textSecondary,
                            )
                        }
                    }

                    Text(
                        text = "Scan this code to review and accept the invitation",
                        style = CoreTypography.bodySecondary,
                        color = CoreTheme.colors.textSecondary,
                    )
                }
            }

            // Invitation Details Card
            CoreCard(
                modifier = Modifier.fillMaxWidth(),
            ) {
                Column(
                    modifier = Modifier.padding(CoreSpacing.md),
                    verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
                ) {
                    Text(
                        text = "INVITATION DETAILS",
                        style = CoreTypography.caption,
                        color = CoreTheme.colors.textSecondary,
                    )

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Text(
                            text = "For:",
                            style = CoreTypography.bodySecondary,
                            color = CoreTheme.colors.textSecondary,
                        )
                        Text(
                            text = state.invitation.childDisplayName,
                            style = CoreTypography.body,
                            color = CoreTheme.colors.textPrimary,
                        )
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Text(
                            text = "Sent to:",
                            style = CoreTypography.bodySecondary,
                            color = CoreTheme.colors.textSecondary,
                        )
                        val formattedDestination = when (state.invitation.deliveryMethod) {
                            InvitationDeliveryMethod.PHONE ->
                                state.invitation.deliveryDestination?.let {
                                    PhoneNumberFormatter.toDisplayFormat(it)
                                } ?: state.invitation.deliveryDestination
                            else -> state.invitation.deliveryDestination
                        }
                        Text(
                            text = formattedDestination ?: "",
                            style = CoreTypography.body,
                            color = CoreTheme.colors.textPrimary,
                        )
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Text(
                            text = "Status:",
                            style = CoreTypography.bodySecondary,
                            color = CoreTheme.colors.textSecondary,
                        )
                        Text(
                            text = state.invitation.status.name.lowercase().replaceFirstChar { it.uppercase() },
                            style = CoreTypography.body,
                            color = CoreTheme.colors.primary,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun Footer(state: ShowInvitationQRCodeState) =
    CorePrimaryButton(
        text = "Done",
        onClick = { state.eventSink(ShowInvitationQRCodeEvent.Done) },
        modifier = Modifier
            .fillMaxWidth(),
    )

@Preview
@Composable
private fun ShowInvitationQRCodeViaPhoneUiPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        ShowInvitationQRCodeUi(
            state = ShowInvitationQRCodeState(
                invitation = Invitation(
                    id = "inv-1",
                    status = InvitationStatus.PENDING,
                    inviterUserProfileId = "user-1",
                    inviterDisplayName = "Parent",
                    childId = "child-1",
                    childDisplayName = "Child",
                    teamId = null,
                    teamType = null,
                    proposedTeamRole = TeamRelationshipRole.ORGANIZER,
                    grantsChildStewardship = false,
                    deliveryMethod = InvitationDeliveryMethod.PHONE,
                    deliveryDestination = "+13125550000",
                    createdAt = Clock.System.now(),
                    createdBy = "user-1",
                    modifiedAt = Clock.System.now(),
                    modifiedBy = "user-1",
                ),
                isLoading = false,
                eventSink = {},
            ),
        )
    }
}

@Preview
@Composable
private fun ShowInvitationQRCodeViaEmailUiPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        ShowInvitationQRCodeUi(
            state = ShowInvitationQRCodeState(
                invitation = Invitation(
                    id = "inv-1",
                    status = InvitationStatus.PENDING,
                    inviterUserProfileId = "user-1",
                    inviterDisplayName = "Parent",
                    childId = "child-1",
                    childDisplayName = "Child",
                    teamId = null,
                    teamType = null,
                    proposedTeamRole = TeamRelationshipRole.ORGANIZER,
                    grantsChildStewardship = false,
                    deliveryMethod = InvitationDeliveryMethod.EMAIL,
                    deliveryDestination = "therapist@example.com",
                    createdAt = Clock.System.now(),
                    createdBy = "user-1",
                    modifiedAt = Clock.System.now(),
                    modifiedBy = "user-1",
                ),
                isLoading = false,
                eventSink = {},
            ),
        )
    }
}
